import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { INSTRUMENTS } from "@/data/brokers";
import { calcHolding } from "@/lib/swapCalc";
import { money } from "@/lib/format";
import { Reveal, SplitLines } from "@/components/motion/Reveal";
import { BrokerMark, Eyebrow, Freshness } from "@/components/shared/Primitives";
import { useBrokers } from "@/context/BrokersContext";

export const SwapPreview = () => {
  const { brokers: BROKERS } = useBrokers();
  const [inst, setInst] = useState("EURUSD");
  const [dir, setDir] = useState("long");
  const [lots, setLots] = useState(1);
  const rows = useMemo(() => BROKERS.map((b) => ({ b, r: calcHolding(b, inst, dir, lots, 7) })).filter((x) => x.r.available).sort((a, b) => b.r.total - a.r.total).slice(0, 4), [BROKERS, inst, dir, lots]);
  const best = rows[0];

  return (
    <section className="section relative overflow-hidden" data-testid="swap-preview">
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,var(--canvas)_0%,var(--canvas-cool)_100%)]" />
      <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow className="mb-4">Swap intelligence</Eyebrow>
            <SplitLines as="h2" inView lines={["The cost of", "holding matters."]} className="t-h2" />
            <p className="mt-6 max-w-md t-lead">Overnight financing quietly decides whether a swing trade is worth taking. Compare normalized swap costs for identical instruments across brokers — including triple-rollover schedules.</p>
            {best && <p className="mt-6 text-sm text-[var(--ink-2)]">Holding <span className="font-mono">{lots.toFixed(2)}</span> lot {INSTRUMENTS.find((i) => i.id === inst).label} {dir} for 7 nights costs <span className="font-mono font-medium text-[var(--ink)]">{money(best.r.total)}</span> at {best.b.short} versus <span className="font-mono font-medium text-[var(--ink)]">{money(rows[rows.length - 1].r.total)}</span> at {rows[rows.length - 1].b.short}.</p>}
            <Link to="/swaps" className="btn-primary mt-10" data-testid="swap-preview-cta">Explore swap rates <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="card p-6 sm:p-8">
            <p className="eyebrow mb-5">Holding-cost comparison · 7 nights</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="eyebrow">Instrument</span>
                <select value={inst} onChange={(e) => setInst(e.target.value)} className="control mt-2 w-full" data-testid="swap-preview-instrument">
                  {INSTRUMENTS.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
                </select>
              </label>
              <div>
                <span className="eyebrow">Direction</span>
                <div className="seg mt-2 w-full" data-testid="swap-preview-direction">
                  <button data-on={dir === "long"} onClick={() => setDir("long")} className="flex-1">Buy</button>
                  <button data-on={dir === "short"} onClick={() => setDir("short")} className="flex-1">Sell</button>
                </div>
              </div>
              <label className="block">
                <span className="eyebrow">Volume (lots)</span>
                <input type="number" min="0.01" step="0.1" value={lots} onChange={(e) => setLots(Math.max(0.01, +e.target.value || 0.01))} className="control mt-2 w-full font-mono" data-testid="swap-preview-lots" />
              </label>
            </div>
            <div className="mt-7 grid grid-cols-12 gap-3 pb-2 font-mono text-[11px] tracking-[0.01em] text-[var(--ink-4)] border-b border-[var(--line)]">
              <span className="col-span-6">Broker</span><span className="col-span-3 text-right">Daily</span><span className="col-span-3 text-right">7-night est.</span>
            </div>
            {rows.map(({ b, r }, i) => (
              <motion.div key={b.slug} layout className="grid grid-cols-12 items-center gap-3 py-3.5 border-b border-[var(--line)] last:border-0" data-testid={`swap-preview-row-${b.slug}`}>
                <div className="col-span-6 flex items-center gap-3">
                  <BrokerMark broker={b} size={30} className="!rounded-lg" />
                  <div><p className="text-sm font-medium">{b.short}</p><Freshness source={b.swapSource} /></div>
                </div>
                <span className={`col-span-3 text-right font-mono text-sm ${r.daily >= 0 ? "text-[var(--pos)]" : "text-[var(--ink)]"}`}>{money(r.daily)}</span>
                <span className={`col-span-3 text-right font-mono text-sm font-medium ${i === 0 ? "text-[var(--cobalt)]" : r.total >= 0 ? "text-[var(--pos)]" : "text-[var(--ink)]"}`}>{money(r.total)}</span>
              </motion.div>
            ))}
            <p className="mt-4 font-mono text-[11px] text-[var(--ink-4)]">Illustrative figures. Estimates include broker-specific triple-rollover days; not live broker quotes.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
